import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { ProviderDisplay } from "./ProviderDisplay";
import { useUtility } from "utils/context/UtilityContext";
import { fetchPublic, useWindowSize } from "utils";
import { Loading } from "../Loading";
import { Header } from "../Header";
import { Subtitle } from "../Subtitle";
import { Footer } from "../footer";
import { Provider } from "utils/types/Provider.types";

// --- Type Definitions ---

interface CategoryData {
  category: string;
  providers: Provider[];
}

// --- Main Component ---

export const ProductsPages = () => {
  const [providersByCategory, setProvidersByCategory] = useState<
    CategoryData[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const { marginBottomHeader } = useUtility();
  const [showDialog, setShowDialog] = useState(false);
  const { isPC } = useWindowSize();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategorizedProviders = async () => {
      setLoading(true);
      try {
        const result = await fetchPublic(`/public/product/providers`);

        if (result.data?.providersByCategory) {
          setProvidersByCategory(result.data.providersByCategory);
        }
      } catch (error) {
        console.error("Failed to fetch providers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategorizedProviders();
    const dialogPreference = localStorage.getItem("sliderDialogShown");
    if (!dialogPreference) {
      setShowDialog(true);
    }
  }, []);

  const handleSelectProvider = (providerName: string) => {
    navigate(`/provider/${providerName}`);
  };

  const handleSort = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);

    const sortedCategories = providersByCategory.map((categoryData) => ({
      ...categoryData,
      providers: [...categoryData.providers].sort((a, b) => {
        return newOrder === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }),
    }));
    setProvidersByCategory(sortedCategories);
  };

  const handleCloseDialog = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      localStorage.setItem("sliderDialogShown", "true");
    }
    setShowDialog(false);
  };

  if (loading) return <Loading />;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`${marginBottomHeader} flex flex-col items-center justify-start gap-4 overflow-x-hidden fancy-scroll`}
    >
      <Header />
      <div
        className={`flex flex-col items-center justify-start gap-4 overflow-x-hidden fancy-scroll p-8 w-full ${
          isPC ? "px-16" : "px-1"
        }`}
      >
        <Dialog open={showDialog} onClose={() => handleCloseDialog(false)}>
          <DialogTitle>Bienvenue !</DialogTitle>
          <DialogContent>
            <Typography>
              Découvrez nos fournisseurs. Vous pouvez alterner entre la vue en
              grille et la vue en carrousel.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => handleCloseDialog(false)}
              sx={{ color: "#104b7b" }}
            >
              OK
            </Button>
            <Button
              onClick={() => handleCloseDialog(true)}
              sx={{ color: "#104b7b" }}
            >
              Ne plus afficher
            </Button>
          </DialogActions>
        </Dialog>

        <Subtitle
          title="Nos Fournisseurs"
          textColor="#104b7b"
          textAlign="items-center"
          textSize="40px"
          loading={loading}
        />

        <Button variant="contained" onClick={handleSort} sx={{ mb: 4 }}>
          Trier par nom ({sortOrder === "asc" ? "A-Z" : "Z-A"})
        </Button>

        {providersByCategory.map(({ category, providers }) => (
          <div key={category} className="w-full">
            <div
              className="cursor-pointer items-center justify-center flex w-full text-center"
              onClick={() => navigate(`/products/${category}`)}
            >
              <Subtitle
                title={category}
                textColor="#104b7b"
                textAlign="items-center"
                textSize="32px"
                loading={loading}
              />
            </div>

            <ProviderDisplay
              providers={providers.map((p) => ({
                name: p.name,
                image: p.image || "",
              }))}
              onSelectProvider={handleSelectProvider}
            />
          </div>
        ))}
      </div>

      <Footer />
    </motion.div>
  );
};
